"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { GuideCtaButton } from "@/components/marketing/guide-start-cta";

export function calculateF10Notifiability(
  workingDays: number,
  peakWorkers: number,
  averageWorkers: number,
) {
  const days = Math.max(0, workingDays || 0);
  const peak = Math.max(0, peakWorkers || 0);
  const average = Math.max(0, averageWorkers || 0);
  const personDays = days * average;
  const thresholdOne = days > 30 && peak > 20;
  const thresholdTwo = personDays > 500;

  return {
    personDays,
    thresholdOne,
    thresholdTwo,
    notifiable: thresholdOne || thresholdTwo,
  };
}

// Allow only digits in the inputs so a partial value (e.g. user deletes "30" to
// type "15" and momentarily has "") doesn't get coerced through Number("") -> 0
// and re-render the field as "0", which traps users in a snap-to-0 rage-click
// loop (select-all → delete → field shows 0 → can't proceed).
const sanitiseDigits = (raw: string) => raw.replace(/[^0-9]/g, "");
const parseDigits = (raw: string) => {
  const trimmed = raw.replace(/^0+(?=\d)/, "");
  if (trimmed === "") return 0;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) ? n : 0;
};

export function F10NotifiabilityCheck({
  slug,
}: {
  /**
   * Slug of the page embedding the widget, reported as `guide_slug` on the
   * post-result prompt's guide_cta_clicked event and used for the signup
   * source param. The guide passes "f10-notification"; the when-to-notify-hse
   * blog post passes its own slug so attribution stays truthful.
   */
  slug: string;
}) {
  // String state, not number state. Empty string is a valid intermediate value
  // that lets the user fully clear the field before typing a new number; the
  // memoised calculation parses it back to a number.
  const [workingDaysInput, setWorkingDaysInput] = useState("30");
  const [peakWorkersInput, setPeakWorkersInput] = useState("8");
  const [averageWorkersInput, setAverageWorkersInput] = useState("4");

  const result = useMemo(
    () =>
      calculateF10Notifiability(
        parseDigits(workingDaysInput),
        parseDigits(peakWorkersInput),
        parseDigits(averageWorkersInput),
      ),
    [averageWorkersInput, peakWorkersInput, workingDaysInput],
  );

  return (
    <section className="my-10 rounded-2xl border border-attn-line bg-attn-bg p-5 sm:p-6">
      <p className="text-sm font-semibold uppercase tracking-wider text-brand-700">
        F10 quick check
      </p>
      <h2 className="mt-2 text-2xl font-bold text-gray-900">
        Is my project notifiable?
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">
        Enter your planned programme and worker numbers. Under CDM 2015, an F10
        is required if the job lasts more than 30 working days with more than 20
        workers on site at any point, or if it exceeds 500 person-days.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {/* type="text" + inputMode="numeric" + pattern keeps the iOS numeric
            keypad but allows the input to hold an empty string mid-edit. type
            ="number" was rejecting empty as invalid and snapping back to 0. */}
        <label className="text-sm font-medium text-gray-800">
          Working days
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={workingDaysInput}
            onChange={(event) => setWorkingDaysInput(sanitiseDigits(event.target.value))}
            className="mt-1 w-full rounded-lg border border-accent/30 bg-white px-3 py-2 text-base text-gray-900 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </label>
        <label className="text-sm font-medium text-gray-800">
          Peak workers
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={peakWorkersInput}
            onChange={(event) => setPeakWorkersInput(sanitiseDigits(event.target.value))}
            className="mt-1 w-full rounded-lg border border-accent/30 bg-white px-3 py-2 text-base text-gray-900 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </label>
        <label className="text-sm font-medium text-gray-800">
          Average workers
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={averageWorkersInput}
            onChange={(event) => setAverageWorkersInput(sanitiseDigits(event.target.value))}
            className="mt-1 w-full rounded-lg border border-accent/30 bg-white px-3 py-2 text-base text-gray-900 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </label>
      </div>

      <div className="mt-5 rounded-xl border border-accent/30 bg-white p-4">
        <p className="text-sm text-gray-600">
          Estimated person-days:{" "}
          <strong className="text-gray-900">{result.personDays}</strong>
        </p>
        <p
          className={`mt-2 text-lg font-bold ${
            result.notifiable ? "text-danger" : "text-done"
          }`}
        >
          {result.notifiable
            ? "Likely notifiable: plan to submit an F10 before work starts."
            : "Likely not notifiable: an F10 is probably not needed on these figures."}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-gray-500">
          This is a planning check, not legal advice. If the programme changes,
          rerun the calculation and keep your CPP up to date.
        </p>

        {/* Close the loop: the reader just engaged with the tool, so the ask
            answers the exact result on screen. Copy rules: The Site Book does
            NOT file the F10 with the HSE (content/help/f10-notification.ts) —
            it pre-fills the data for the HSE's online form and records when
            you have notified — and the F10 tools are Pro-gated, so the
            notifiable variant names the tier. The not-notifiable variant
            pivots to the CPP, which every construction project needs
            regardless of notifiability. */}
        <div
          data-guide-cta="post-tool"
          className="mt-4 rounded-lg border border-accent/30 bg-accent-light p-4"
        >
          {result.notifiable ? (
            <>
              <p className="text-sm leading-relaxed text-gray-800">
                The Site Book pre-fills your F10 data for the HSE&rsquo;s
                online form and records when you&rsquo;ve notified — F10 tools
                are on Pro and above.
              </p>
              <GuideCtaButton
                slug={slug}
                position="post-tool"
                ctaCopy="The Site Book pre-fills your F10 data for the HSE's online form and records when you've notified — F10 tools are on Pro and above."
                label="Get started →"
                className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-[10px] bg-accent px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-accent-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-dark"
              />
            </>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-gray-800">
                No F10 needed on these figures — but every construction project
                still needs a construction phase plan. The Site Book generates
                yours from your project details.
              </p>
              <GuideCtaButton
                slug={slug}
                position="post-tool"
                ctaCopy="No F10 needed on these figures — but every construction project still needs a construction phase plan."
                label="See how →"
                href="/features/cpp"
                className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-[10px] bg-accent px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-accent-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-dark"
              />
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Link
          href="/guides/f10-notification"
          className="inline-flex items-center justify-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-dark"
        >
          Read the full F10 guide
        </Link>
        <Link
          href="/free/cpp-template"
          className="inline-flex items-center justify-center rounded-lg border border-accent/30 bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-accent-light"
        >
          Get the CPP template
        </Link>
      </div>
    </section>
  );
}
